import { MimeType, TemplateData } from 'easy-template-x'
import { capturePage } from 'components/spreadsheet/components/custom-view/capture'
import { getChartDimensions } from './helpers'
import { DocxPlaceholders } from './types'

/** A Custom View the backend left a placeholder for (`extractPlaceholdersFromDocx(buffer, 'CUSTOM_VIEW')`). */
export type CustomViewPlaceholder = DocxPlaceholders[number]

/** The placeholder without its braces, as the template data is keyed. */
const templateKey = ({ tableId, viewId, height, columns, instance }: CustomViewPlaceholder) =>
  `CUSTOM_VIEW tableId=${tableId} viewId=${viewId} height=${height} columns=${columns} instance=${instance}`

/** Pictures drawn at twice their size on screen, so they stay sharp in print. */
const CAPTURE_SCALE = 2

/** Each Custom View on screen, numbered in document order per view, as the backend numbers its placeholders. */
const findCustomViews = (): Map<string, Element> => {
  const found = new Map<string, Element>()
  const counts: Record<string, number> = {}
  document.querySelectorAll('[data-custom-view-capture][data-table-id][data-view-id]').forEach((element) => {
    const view = `tableId=${element.getAttribute('data-table-id')} viewId=${element.getAttribute('data-view-id')}`
    counts[view] = (counts[view] ?? 0) + 1
    found.set(`${view} instance=${counts[view]}`, element)
  })
  return found
}

const placeholderView = ({ tableId, viewId, instance }: CustomViewPlaceholder) =>
  `tableId=${tableId} viewId=${viewId} instance=${instance}`

/** Waits for every placeholder's Custom View to be on screen (embeds load as they render), up to `maxAttempts`. */
const waitForCustomViews = async (
  placeholders: CustomViewPlaceholder[],
  maxAttempts: number,
  intervalMs: number
): Promise<Map<string, Element>> => {
  let found = findCustomViews()
  for (let attempt = 1; attempt < maxAttempts; attempt++) {
    if (placeholders.every((placeholder) => found.has(placeholderView(placeholder)))) break
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
    found = findCustomViews()
  }
  return found
}

/**
 * Pictures of the document's Custom Views, keyed by placeholder, each as wide as its place on the page. One that
 * can't be pictured says so in the document rather than stopping the export.
 */
export const captureCustomViews = async (
  placeholders: CustomViewPlaceholder[],
  pageFormat: string,
  pageOrientation: string,
  onProgress: (current: number, total: number) => void = () => {},
  { maxAttempts = 60, intervalMs = 500 } = {}
): Promise<TemplateData> => {
  const templateData: TemplateData = {}
  if (!placeholders.length) return templateData
  const found = await waitForCustomViews(placeholders, maxAttempts, intervalMs)
  for (const [index, placeholder] of placeholders.entries()) {
    onProgress(index, placeholders.length)
    const element = found.get(placeholderView(placeholder))
    try {
      if (!element) throw new Error("The Custom View didn't load.")
      // On screen while it's pictured: browsers stop drawing frames out of view, which would hold up its charts.
      element.scrollIntoView({ block: 'center' })
      const picture = await capturePage(element, CAPTURE_SCALE)
      // As wide as its column on the page, keeping the page's shape.
      const { width } = getChartDimensions(pageFormat, pageOrientation, undefined, placeholder.columns)
      const height = Math.round((width * picture.height) / picture.width)
      templateData[templateKey(placeholder)] = {
        _type: 'image',
        source: picture.data,
        format: MimeType.Png,
        width,
        height
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error)
      templateData[templateKey(placeholder)] = `This Custom View couldn't be included in the export. ${reason}`
    }
  }
  onProgress(placeholders.length, placeholders.length)
  return templateData
}
